import React, { useCallback, useEffect, useState } from "react"
import { ToastContainer, toast } from "react-toastify"
import { request } from "../../services/request"
import { DeviceDataResponse, DeviceData } from "../../services/authinterface"
import Table from "../../components/common/Table"
import { useDebounce } from "../../hooks/useDebounce"
import { useDispatch } from "react-redux"
import { changeloaderState } from "../../store/loaderSlice"
import { AppDispatch } from "../../store/store"
import { Link } from "react-router-dom";
import { donwloadCSV } from "../../utils/exportFunctions"
import jsPDF from "jspdf"
import autoTable from "jspdf-autotable"
import PrimaryButton from "../../components/subComponents/PrimaryButton"

const Devices: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>()
    const [devices, setDevices] = useState<DeviceData[]>([])
    const [page, setPage] = useState(1)
    const [rowsPerPage, setRowsPerPage] = useState(25)
    const [totalItems, setTotalItems] = useState(100)
    const [search, setSearch] = useState("")
    const debounceText = useDebounce(search, 500)
    const [sort, setSort] = useState("DESC")
    const [sortCol, setSortCol] = useState("createdAt")

    const fetchDeviceData = useCallback(async () => {
        try {
            dispatch(changeloaderState(true))
            const params = {
                page: page,
                limit: rowsPerPage,
                search: debounceText,
                sort: sort,
                sortCol: sortCol,
            }
            const response = await request<DeviceDataResponse>({
                url: "/adminCustomers/getAllDevices",
                method: "POST",
                data: params,
            })

            if (response.success) {
                setDevices(response.data)
                setTotalItems(response.total)
            } else {
                toast.error("Failed to fetch: " + response.message)
            }
        } catch (error: any) {
            toast.error(`Error: ${error.message}`)
        } finally {
            dispatch(changeloaderState(false))
        }
    }, [dispatch, page, rowsPerPage, debounceText, sort, sortCol])

    useEffect(() => {
        fetchDeviceData()
    }, [page, rowsPerPage, debounceText, sort, fetchDeviceData, sortCol, sort])

    useEffect(() => {
        if (debounceText) {
            setPage(1)
        }
    }, [debounceText])

    const fetchAllDevices = async () => {
        try {
            const params = {
                page: 1,
                search: debounceText,
                limit: 100000,
                sort: sort,
                sortCol: sortCol,
            }

            const response = await request<DeviceDataResponse>({
                url: "/adminCustomers/getAllDevices",
                method: "POST",
                data: params,
            })

            if (response.success) {
                return response.data
            } else {
                toast.error("Failed to fetch devices: " + response.message)
                return []
            }
        } catch (error: any) {
            toast.error(`Error fetching all devices: ${error.message}`)
            return []
        }
    }

    const exportCSV = async () => {
        const allData = await fetchAllDevices()

        if (!allData.length) {
            toast.error("No data available to export")
            return
        }

        const headers = ["S. No.", "Name", "Device Type", "Aws Arn", "Device Token"]

        const rows = allData.map((device, index) => [
            index + 1,
            !device?.usersDevice?.first_name && !device?.usersDevice?.last_name
                ? "-"
                : `${device?.usersDevice?.first_name || ""} ${device?.usersDevice?.last_name || ""}`,
            device.deviceType,
            device.awsArn,
            device.deviceToken,
        ])

        donwloadCSV(headers, rows, "Devices.csv")
    }

    const exportPDF = async () => {
        const allData = await fetchAllDevices()

        if (!allData.length) {
            toast.error("No data available to export")
            return
        }

        const headers = ["Sr. No.", "Name", "Device Type", "Aws Arn", "Device Token"]

        const rows = allData.map((device, index) => [
            index + 1,
            !device?.usersDevice?.first_name && !device?.usersDevice?.last_name
                ? "-"
                : `${device?.usersDevice?.first_name || ""} ${device?.usersDevice?.last_name || ""}`,
            device.deviceType,
            device.awsArn,
            device.deviceToken,
        ])

        const doc = new jsPDF()

        // Title
        doc.setFontSize(16)
        doc.text("Devices", 14, 15)

        // Table
        autoTable(doc, {
            head: [headers],
            body: rows,
            startY: 25,
            styles: {
                fontSize: 10,
                cellPadding: 3,
            },
            headStyles: {
                fillColor: [41, 128, 185], // blue header
                textColor: 255,
            },
            columnStyles: {
                3: { cellWidth: 70, overflow: 'linebreak' }, // 3 is the index for 'Aws Arn'
                4: { cellWidth: 70, overflow: 'linebreak' }  // 4 is the index for 'Device Token'
            }
        })

        doc.save("Devices")
    }

    const columns = [
        {
            name: "Name",
            value: (device: DeviceData) => (
                !device?.usersDevice?.first_name && !device?.usersDevice?.last_name
                    ? "-"
                    : (
                        <Link className="device-link" to={`/customer/view/${device?.userAccountId}`}>
                            {(device?.usersDevice?.first_name || "") +
                                " " +
                                (device?.usersDevice?.last_name || "")}
                        </Link>
                    )
            ),
            id: "name",
        },
        {
            name: "Device Type",
            value: (device: DeviceData) =>
                device?.deviceType,
            id: "Sensor_Status",
        },
        {
            name: "Aws Arn",
            value: (device: DeviceData) =>
                device?.awsArn,
            id: "Sensor_Status",
            className: "long-text-devices"
        },
        {
            name: "Device Token",
            value: (device: DeviceData) =>
                device?.deviceToken,
            id: "Sensor_Status",
            className: "long-text-devices"
        },
    ]

    return (
        <section className=' dashboard-section dashboard-item '>
            <ToastContainer position='top-right' autoClose={2000} theme='light' />

            <div className='container'>
                <div className='row'>
                    <div className='col-12'>
                        <div className='dashboard-table-title-section'>
                            <h2>Devices</h2>
                            <div className='dashboard-table-title-right-side d-flex gap-2'>
                                <PrimaryButton
                                    label='Export CSV'
                                    className='max-w-max-content'
                                    onClick={exportCSV}
                                />
                                <PrimaryButton
                                    label='Export PDF'
                                    className='max-w-max-content'
                                    onClick={exportPDF}
                                />
                            </div>
                        </div>
                    </div>
                    <div className='col-12 mt-4'>
                        <div className='table-container-main'>
                            <div className='table-details-section'></div>
                            <Table
                                rowData={devices}
                                columns={columns}
                                page={page}
                                setPage={setPage}
                                rowsPerPage={rowsPerPage}
                                setRowsPerPage={setRowsPerPage}
                                totalItems={totalItems}
                                setSortColumn={setSortCol}
                                setSortOrder={setSort}
                                sortColumn={sortCol}
                                sortOrder={sort}
                                isSearch
                                search={search}
                                setSearch={setSearch}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default Devices
